import { usePartyCustomization } from '@hooks/usePartyCustomization';
import queryString from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { useGetPartnerByGuidQuery } from '@/store/api/observerApi';
import { resolveRoleFromQuery } from '@/utils/volunteerRole';

import { type IActions } from '../../../interface/IAction';
import { type IStates } from '../../../interface/IReducer';
import { applyPartyTheme, clearPartyTheme } from '../../../theme/applyPartyTheme';
import { useTheme } from '../../../theme/ThemeContext';
import DataWrapper from '../../../wrapper/data-wrapper';
import CreateAgent from './create';
import { readTrustedInvite } from './trustedInvite';

interface LPChild {
	states?: IStates;
}

const AgentApplicationChild: React.FC<LPChild> = ({ states, ...props }) => {
	const { getPartyGuid, getElectionGuid, setPageSection } = props as unknown as IActions;

	const location = useLocation();
	const { search } = location;
	const { party, election, observer } = queryString.parse(search);

	const role = resolveRoleFromQuery(search);
	const isObserver = role === 'observer';
	const partnerGuid = observer as string | undefined;

	// `trusted=1` is the party's trusted-agent link. Read once from the URL and
	// held in state — it is never an input, and it is a party-agent concept, so
	// the observer lane ignores it even if someone appends it to a partner link.
	const isTrustedInvite = useMemo(() => readTrustedInvite(search), [search]) && !isObserver;

	const { resolvedTheme } = useTheme();
	const { data: customization } = usePartyCustomization(party as string, election as string);

	// Observer lane resolves its developmental partner via RTK Query; the agent
	// lane keeps the legacy party-guid thunk. Election is resolved for both.
	const { data: partnerResponse } = useGetPartnerByGuidQuery(partnerGuid ?? '', {
		skip: !isObserver || !partnerGuid,
	});
	const partnerData = partnerResponse?.data ?? null;

	useEffect(() => {
		getElectionGuid(election as string);
		if (!isObserver) getPartyGuid(party as string);
	}, []);

	// Recolour the page at runtime from the party's published customization.
	// Null customization (or invalid colours) leaves the iElect defaults intact.
	// Re-runs when the resolved theme flips so deep colours re-adapt for dark bg.
	useEffect(() => {
		applyPartyTheme(customization, resolvedTheme);
		return () => {
			clearPartyTheme();
		};
	}, [customization, resolvedTheme]);

	return (
		<div>
			<CreateAgent
				states={states}
				actions={props as unknown as IActions}
				guid={'?PartyGuid=' + party + '&ElectionGuid=' + election}
				customization={customization}
				role={role}
				partnerData={partnerData}
				isTrustedInvite={isTrustedInvite}
			/>
		</div>
	);
};

const AgentApplicationMemo = React.memo(AgentApplicationChild);

const AgentApplication: React.FC = () => (
	<DataWrapper>
		<AgentApplicationMemo />
	</DataWrapper>
);

export default AgentApplication;
